import { useEffect, type RefObject } from 'react';
import { applyStreamPlaybackForPageVisibility, pageHidesStream } from './pauseStreamWhenPageHidden';

/** Pauses video and game audio while the document is hidden, and resumes them when it is shown. */
export function usePauseStreamWhenPageHidden(
  videoRef: RefObject<HTMLVideoElement | null>,
  audioRef: RefObject<HTMLAudioElement | null>,
): void {
  useEffect(() => {
    const stopPlayWhileHidden = (event: Event) => {
      if (pageHidesStream(document.visibilityState)) {
        (event.currentTarget as HTMLMediaElement).pause();
      }
    };

    const sync = () => {
      applyStreamPlaybackForPageVisibility(document.visibilityState, videoRef.current, audioRef.current);
    };

    const video = videoRef.current;
    const audio = audioRef.current;
    video?.addEventListener('play', stopPlayWhileHidden);
    audio?.addEventListener('play', stopPlayWhileHidden);
    document.addEventListener('visibilitychange', sync);
    if (pageHidesStream(document.visibilityState)) sync();

    return () => {
      video?.removeEventListener('play', stopPlayWhileHidden);
      audio?.removeEventListener('play', stopPlayWhileHidden);
      document.removeEventListener('visibilitychange', sync);
    };
  }, [audioRef, videoRef]);
}
